import { List } from "react-virtualized";
import { SearchResults } from "../../models/search";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { clsx } from "clsx";
import { ArrowDropDown, ArrowRight } from "@mui/icons-material";
import { useState, useRef, useEffect } from "react";

export interface TreeFilterNode {
  key: string;
  column: keyof SearchResults;
  label: string;
  value: string;

  selected: boolean;
  applied: boolean;
  indeterminate: boolean;
  allow_select?: boolean;

  children?: TreeFilterNode[];
  open: boolean;
}

export type TreeSelectNodeProps = {
  model: TreeFilterNode;
  handleFilterStateChange: (column: keyof SearchResults, filterVal: string, isSelected: boolean) => void;
  tier?: number;

  handleToggleOpenChildren?: (node: TreeFilterNode) => void;
  isOpen?: (node: TreeFilterNode) => boolean;

  filterChildrenByString?: string;
  disabled?: boolean;
  maxContentWidth?: number;
};

const TreeSelectNode = ({
  model,
  handleFilterStateChange,
  tier,
  handleToggleOpenChildren = () => {},
  isOpen = () => false,
  filterChildrenByString,
  disabled = false,
  maxContentWidth = 0,
}: TreeSelectNodeProps) => {
  const hasChildren = (model?.children?.length ?? 0) > 0;
  const current_tier = tier || 1;

  const open = hasChildren ? isOpen(model) : false;

  const children_nodes = (model.children || []).filter((_child) =>
    filterChildrenByString ? _child.label.toLocaleLowerCase().includes(filterChildrenByString.toLowerCase()) : true,
  );

  const parent_node = (
    <Box
      className={clsx(["TreeSelect-Node", `TreeSelect-Tier-${tier}`])}
      key={current_tier}
      sx={{
        minWidth: maxContentWidth,
        display: "flex",
        flexDirection: "row",
        whiteSpace: "nowrap",
      }}
    >
      {/* Expansion Toggle */}
      {hasChildren ? (
        <Box
          onClick={() => handleToggleOpenChildren(model)}
          sx={{ cursor: "pointer", "& .MuiSvgIcon-root": { verticalAlign: "middle" } }}
        >
          {open ? <ArrowDropDown /> : <ArrowRight />}
        </Box>
      ) : (
        <Box sx={{ ".TreeSelect-Tier-1 > &": { height: "28px", width: "24px" } }}></Box>
      )}

      {/* Filter Selection Input */}
      <FormControlLabel
        control={
          model.allow_select !== false ? (
            <Checkbox
              name={`${model.column}-${model.value}`}
              size="small"
              checked={model.selected}
              onChange={(e, checked) => handleFilterStateChange(model.column, model.value, !checked)}
              indeterminate={model.indeterminate}
              disabled={disabled}
            />
          ) : (
            <></>
          )
        }
        label={
          <span
            title={model.label}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-block",
              verticalAlign: "middle",
            }}
          >
            {model.label}
          </span>
        }
        labelPlacement="end"
        sx={model.allow_select !== false ? {} : { paddingLeft: "14px", cursor: "default" }}
      />
    </Box>
  );

  const parentLabelIncludedInString = filterChildrenByString
    ? model.label.toLocaleLowerCase().includes(filterChildrenByString.toLowerCase())
    : true;

  const showParentNode = children_nodes.length > 0 || parentLabelIncludedInString;

  return [
    ...(showParentNode ? [parent_node] : []),
    ...(open
      ? children_nodes.map((childNodeModel, i) => (
          <TreeSelectNode
            key={`${current_tier}-${i}`}
            model={childNodeModel}
            tier={current_tier + 1}
            handleFilterStateChange={handleFilterStateChange}
            handleToggleOpenChildren={handleToggleOpenChildren}
            isOpen={isOpen}
            disabled={disabled}
            maxContentWidth={maxContentWidth}
          />
        ))
      : []),
  ];
};

export interface TreeSelectFilterVirtualProps {
  height: number;
  model: TreeFilterNode[];
  filterChildrenByString?: string;
  handleFilterStateChange: (column: keyof SearchResults, filterVal: string, isSelected: boolean) => void;
  disabled?: boolean;
}

export default function TreeSelectFilterVirtual({
  height,
  model,
  handleFilterStateChange,
  filterChildrenByString,
  disabled,
}: TreeSelectFilterVirtualProps) {
  const containerWidth = 288;
  const [treeState, setTreeState] = useState(new Map<string, boolean>());
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxContentWidth, setMaxContentWidth] = useState(containerWidth);

  const isOpen = (node: TreeFilterNode) => {
    return treeState.get(node.value) ?? false;
  };

  const handleToggleOpenChildren = (node: TreeFilterNode) => {
    const key = node.value;
    setTreeState((_state) => {
      const newState = new Map(_state.entries());
      newState.set(key, !(newState.get(key) ?? false));
      return newState;
    });
  };

  // Flattened list for virtualized rendering, passing maxContentWidth
  const list = model.flatMap((node) =>
    TreeSelectNode({
      handleFilterStateChange,
      model: node,
      handleToggleOpenChildren,
      isOpen,
      filterChildrenByString,
      disabled,
      maxContentWidth,
    }),
  );

  // After rendering, measure widest row and update maxContentWidth state
  useEffect(() => {
    if (!containerRef.current) return;

    const nodes = containerRef.current.querySelectorAll(".TreeSelect-Node");
    let widest = containerWidth;

    nodes.forEach((node) => {
      if (node.scrollWidth > widest) {
        widest = node.scrollWidth;
      }
    });

    // Only update if widest is larger than containerWidth
    if (widest > containerWidth && widest !== maxContentWidth) {
      setMaxContentWidth(widest);
    }
    // Reset to containerWidth if widest is less or equal to containerWidth
    else if (widest <= containerWidth && maxContentWidth !== containerWidth) {
      setMaxContentWidth(containerWidth);
    }
  }, [list, filterChildrenByString]);

  const rowRenderer = ({ key, index, style }: RowRenderProps) => {
    return (
      <div key={key} style={style}>
        {list[index]}
      </div>
    );
  };

  const count = list.length;

  return (
    <Box
      ref={containerRef}
      sx={{
        width: containerWidth,
        overflowX: "auto",
        overflowY: "auto",
        maxHeight: 200,
        border: "1px solid #fff",
        padding: "0 0 10px 0",

        "&::-webkit-scrollbar": {
          height: "6px",
          width: "6px",
        },
        "&::-webkit-scrollbar:vertical": {
          display: "none",
        },
        "& .MuiFormControlLabel-root": {
          display: "block",
          margin: 0,
        },
        "& .MuiCheckbox-root": {
          padding: "4px",
          marginRight: "4px",
        },
        "& .TreeSelect-Node": {
          whiteSpace: "nowrap",
          display: "flex",
          flexDirection: "row",
          paddingRight: 8,
        },
        "& .TreeSelect-Tier-1": { marginLeft: `${3.125 * 0}em` },
        "& .TreeSelect-Tier-2": { marginLeft: `${3.125 * 1}em` },
        "& .TreeSelect-Tier-3": { marginLeft: `${3.125 * 2}em` },
        "& .TreeSelect-Tier-2 span": {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 200,
          display: "inline-block",
          verticalAlign: "middle",
        },
      }}
    >
      <List
        width={maxContentWidth}
        height={Math.min(height, 28 * count)}
        rowCount={count}
        rowHeight={28}
        rowRenderer={rowRenderer}
        style={{
          whiteSpace: "nowrap",
        }}
      />
    </Box>
  );
}

interface RowRenderProps {
  key: React.Key; // Unique key within array of rows
  index: number; // Index of row within collection
  isScrolling: boolean; // The List is currently being scrolled
  isVisible: boolean; // This row is visible within the List (eg it is not an overscanned row)
  style: React.CSSProperties; // Style object to be applied to row (to position it)
}
